<template lang="pug">
include ../pug/svg
section#settings
	Form.wrapper(ref="organisationForm" :validation-schema="organisationSchema" v-slot="{ values, errors, meta }")
		div.fieldset.c3
			h4 {{terminology('organisation','organisation','singular')}}
			FieldText(name="name" v-model="name" :required="true" :cancelled="cancelled" :errors="errors" @keyup="enableCheck" :label="terminology('organisation', 'organisation', 'singular') + ' Name'")

			FieldText(name="subdomain" v-model="subdomain" :required="true" :cancelled="cancelled" :errors="errors" @keyup="enableCheck" :disabled="true" label="Subdomain")

			FieldText(name="websiteUrl" v-model="websiteUrl" :cancelled="cancelled" :errors="errors" @keyup="enableCheck" :disabled="true" label="Website URL")

			FieldText(name="emailGeneral" v-model="emailGeneral" :cancelled="cancelled" :errors="errors" @keyup="enableCheck" label="Email (General)")

			FieldText(name="emailSales" v-model="emailSales" :cancelled="cancelled" :errors="errors" @keyup="enableCheck" label="Email (Sales)")

			FieldText(name="emailSupport" v-model="emailSupport" :cancelled="cancelled" :errors="errors" @keyup="enableCheck" label="Email (Support)")

			FieldText(name="phoneGeneral" v-model="phoneGeneral" :cancelled="cancelled" :errors="errors" @keyup="enableCheck" label="Telephone (General)")

			FieldText(name="phoneSales" v-model="phoneSales" :cancelled="cancelled" :errors="errors" @keyup="enableCheck" label="Telephone (Sales)")

			FieldText(name="phoneSupport" v-model="phoneSupport" :cancelled="cancelled" :errors="errors" @keyup="enableCheck" label="Telephone (Support)")
		div.fieldset-wrapper
			div.fieldset.c3(v-if="licenseField")
				h4 Media Upload Limits
				FieldText(name="imgMb" v-model="imgMb" :numeric="true" :cancelled="cancelled" :errors="errors" @keyup="enableCheck" placeholder="Unlimited" label="Image Filesize Limit (MB)" append="MB")
				FieldText(name="mp3Mb" v-model="mp3Mb" :numeric="true" :cancelled="cancelled" :errors="errors" @keyup="enableCheck" placeholder="Unlimited" label="Audio Filesize Limit (MB)" append="MB")
				FieldText(name="mp4Mb" v-model="mp4Mb" :numeric="true" :cancelled="cancelled" :errors="errors" @keyup="enableCheck" placeholder="Unlimited" label="Video Filesize Limit (MB)" append="MB")
			div.fieldset(:class="(licenseField)?'c1':'c2'")
				h4 {{terminology('user','mentor','singular')}} Relationships
				FieldBoolean(
					v-model="assignmentIndividual"
					:label="'Define '+terminology('user','learner','singular')+'/'+terminology('user','mentor','singular')+' relationships'"
					yesLabel="Yes"
					noLabel="No"
					@fieldUpdated="changeAssignmentIndividual")
				FieldBoolean(
					v-model="assignmentAuto"
					:label="'Manually assign '+terminology('user','learner','plural')+' to '+terminology('user','mentor','plural')"
					yesLabel="No"
					noLabel="Yes"
					:reverse="true"
					:disabled="!assignmentIndividual"
					@fieldUpdated="enableCheck")
			div.fieldset.c1
				h4 Email Notifications
				FieldBoolean(
					v-model="notificationAnonymous"
					label="Personally identifiable information"
					yesLabel="Prevent inclusion"
					noLabel="Allow inclusion"
					:reverse="true"
					@fieldUpdated="enableCheck")
			div.fieldset.c1(v-if="licenseField")
				h4 License Allocation
				FieldText(name="userLimit" v-model="userLimit" :numeric="true" :cancelled="cancelled" :errors="errors" @keyup="enableCheck" placeholder="Unlimited" :label="terminology('user','user','singular')+' Limit'")
	span.req Required
</template>

<script>
import { Form } from 'vee-validate';
import * as Yup from 'yup';
import { mapFields } from 'vuex-map-fields';	
import FieldText from '../components/FieldText';
import FieldBoolean from '../components/FieldBoolean';

export default {
	name: 'ManageOrganisationSettings',
	props: ['cancelled'],
	emits: ['storeUpdated'],
	components: {
		Form,
		FieldText,
		FieldBoolean,
	},
	data() {
		const req = 'Required';
		const inv = 'Invalid';
		const organisationSchema = Yup.object().shape({
			name: Yup.string().required(req).nullable(),
			subdomain: Yup.string().required(req).nullable(),
//			websiteUrl: Yup.string().email(inv).required(req).nullable(),
			emailGeneral: Yup.string().email(inv).nullable(),
			emailSales: Yup.string().email(inv).nullable(),
			emailSupport: Yup.string().email(inv).nullable(),
			phoneGeneral: Yup.string().nullable(),
			phoneSales: Yup.string().nullable(),
			phoneSupport: Yup.string().nullable(),
//			userLimit: Yup.string().nullable(),
		});
		
		return {
			organisationSchema,
			invalidFields: [],
		}
	},
	mounted() {
		this.$store.dispatch('gui/setHeader', {
			title: this.terminology('interface', 'manage', 'singular') + ' ' + this.terminology('organisation', 'organisation', 'singular'),
			backRoute: {
				text: 'Dashboard',
				name: 'Dashboard',
			},
		});		
	},
	computed: {
		userRole() {
			return this.$store.getters['auth/getUserRole'];
		},
		licenseField() {
			return ( this.userRole.seniority <= 20 );
		},
		imgMb: {
			get() {
				return ( this.maxBytesImg / 1000000 );
			},
			set(mb) {
				this.maxBytesImg = (mb * 1000000);
			}
		},
		mp3Mb: {
			get() {
				return ( this.maxBytesMp3 / 1000000 );
			},
			set(mb) {
				this.maxBytesMp3 = (mb * 1000000);
			}
		},
		mp4Mb: {
			get() {
				return ( this.maxBytesMp4 / 1000000 );
			},
			set(mb) {
				this.maxBytesMp4 = (mb * 1000000);
			}
		},
		...mapFields('gui', [ // get/set vuex store
			'organisation',
			'organisation.name',
			'organisation.subdomain',
			'organisation.websiteUrl',
			'organisation.emailGeneral',
			'organisation.emailSales',
			'organisation.emailSupport',
			'organisation.phoneGeneral',
			'organisation.phoneSales',
			'organisation.phoneSupport',
			'organisation.userLimit',
			'organisation.assignmentAuto',
			'organisation.assignmentIndividual',
			'organisation.notificationAnonymous',
			'organisation.maxBytesImg',
			'organisation.maxBytesMp3',
			'organisation.maxBytesMp4',
		]),
	},
	watch: {
		cancelled: {
			// parent triggered cancel
			handler(val) {
				if (!val) { // store reset complete
					const form = this.$refs.organisationForm;
					const errors = form.getErrors();
					
					for(const field in form.getValues()) {
						form.setFieldTouched(field, false);
						
						if (field in errors) {
							form.setFieldError(field, '-');					
						}
					}
				}
			},
			deep: true,
		},
	},
	methods: {
		terminology(groupKey, termKey, varKey) {
			return this.$store.getters['gui/customTerm'](groupKey, termKey, varKey);
		},
		enableCheck() {
			const meta = this.$refs.organisationForm.getMeta();
			this.$emit('storeUpdated', {
				screen: 'settings',
				valid: meta.valid,
			});
		},
		changeAssignmentIndividual() {
			if (!this.assignmentIndividual) {
				this.assignmentAuto = 1;
			}
			this.enableCheck();
		},
	},
}
</script>

<style lang="scss">
#settings {
	.fieldset-wrapper {
		align-items: flex-start;
	}
	/*
	form {
		display: flex;
		flex-wrap: wrap;
		margin-top: 30px;
	}
	input[disabled] {
		cursor: not-allowed;
	}
	@media screen and (max-width: 614px) {
		form {
			flex-direction: column;
		}
		.field {
			width: 100%;
			max-width: none;
		}
	}
	@media screen and (min-width: 615px) and (max-width: 1109px) {
		.field {
			width: calc(50% - 7.5px);
			&:nth-child(odd) {
				margin-right: 15px;
			}
		}
	}
	@media screen and (min-width: 1110px) {
		.field {
			width: calc(33.333% - 10px);
			margin-right: 15px;
			&:nth-child(3n+3) {
				margin-right: 0;
			}
		}
	}*/
}
</style>
